import type { CharacterDraft } from "@/features/characters/store"
import type { CharacterPersonality } from "@/shared/types/character"
import {
  HAIR_STYLE_OPTIONS,
  HAIR_COLORS,
  EYE_COLORS,
  BODY_TYPE_OPTIONS,
  BREAST_SIZE_OPTIONS,
  BUTT_SIZE_OPTIONS,
  PERSONALITY_CHIP_DATA,
  OCCUPATION_OPTIONS,
  RELATIONSHIP_OPTIONS,
  HOBBY_OPTIONS,
  FETISH_OPTIONS,
  VOICE_FALLBACK_OPTIONS,
} from "@/features/create/components/appearance-step"

const SKIN_TONE_IDS = ["2", "4", "6", "7", "9", "10"] as const

const FEMALE_FIRST_NAMES = [
  "Alexis", "Aria", "Aurora", "Bella", "Bianca", "Celeste", "Chloe", "Diana",
  "Elena", "Ember", "Fiona", "Freya", "Grace", "Harper", "Isabella", "Ivy",
  "Jade", "Jasmine", "Kira", "Lila", "Luna", "Maya", "Mila", "Nadia",
  "Nora", "Olivia", "Penelope", "Quinn", "Riley", "Ruby", "Sienna", "Sophia",
  "Stella", "Tessa", "Valeria", "Violet", "Willow", "Zara", "Amber", "Scarlett",
] as const

const NAME_SUFFIXES = [
  "", "", "", // weight toward single names
  "Rose", "Skye", "Blake", "Lane", "Reed", "Fox", "Vale", "Noir",
] as const

const AGE_MIN = 18
const AGE_MAX = 35

function pickRandom<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]!
}

function pickRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function pickRandomMany<T>(items: readonly T[], count: number): T[] {
  const pool = [...items]
  const picked: T[] = []
  const limit = Math.min(count, pool.length)
  while (picked.length < limit) {
    const index = Math.floor(Math.random() * pool.length)
    picked.push(pool.splice(index, 1)[0]!)
  }
  return picked
}

function pickRandomOptionValue<T extends { value: string }>(
  options: readonly T[],
  exclude: string[] = ["none"]
): string {
  const eligible = options.filter((o) => !exclude.includes(o.value))
  return pickRandom(eligible).value
}

/** Returns true when the user has not made any meaningful choices yet. */
export function isPristineCharacterDraft(draft: CharacterDraft): boolean {
  return (
    !draft.style &&
    !draft.ethnicity &&
    !draft.hairStyle &&
    !draft.hairColor &&
    !draft.eyeColor &&
    !draft.bodyType &&
    !draft.name
  )
}

/** Picks a random display name suited for the default female character flow. */
export function generateRandomCharacterName(): string {
  const first = pickRandom(FEMALE_FIRST_NAMES)
  const suffix = pickRandom(NAME_SUFFIXES)
  return suffix ? `${first} ${suffix}` : first
}

export function pickRandomVoiceId(voices: ReadonlyArray<{ id: string }>): string {
  return pickRandom(voices).id
}

/** Fills only empty wizard fields; never overwrites style or existing user choices. */
export function fillMissingRandomDefaults(
  draft: CharacterDraft
): Partial<CharacterDraft> {
  const random = buildRandomCharacterDefaults()
  const updates: Partial<CharacterDraft> = {}

  if (!draft.name?.trim()) updates.name = random.name
  if (
    !Number.isFinite(draft.age) ||
    draft.age < AGE_MIN ||
    draft.age > AGE_MAX
  ) {
    updates.age = random.age
  }
  if (!draft.skinTone) updates.skinTone = random.skinTone
  if (!draft.hairStyle) updates.hairStyle = random.hairStyle
  if (!draft.hairColor) updates.hairColor = random.hairColor
  if (!draft.eyeColor) updates.eyeColor = random.eyeColor
  if (!draft.bodyType) updates.bodyType = random.bodyType
  if (!draft.breastSize) updates.breastSize = random.breastSize
  if (!draft.buttSize) updates.buttSize = random.buttSize
  if (!draft.personality?.length) updates.personality = random.personality
  if (!draft.voiceId) updates.voiceId = random.voiceId
  if (!draft.occupation) updates.occupation = random.occupation
  if (!draft.relationshipType) updates.relationshipType = random.relationshipType
  if (!draft.hobby) updates.hobby = random.hobby
  if (!draft.fetish) updates.fetish = random.fetish

  return updates
}

/**
 * Random pre-selections for wizard fields except identity-facing `style` and
 * `ethnicity`, which must remain empty until the user chooses them.
 */
export function buildRandomCharacterDefaults(): Partial<CharacterDraft> {
  const personalityCount = pickRandomInt(2, 4)
  const personality = pickRandomMany(
    PERSONALITY_CHIP_DATA.map((t) => t.value),
    personalityCount
  ) as CharacterPersonality[]

  return {
    name: generateRandomCharacterName(),
    skinTone: pickRandom(SKIN_TONE_IDS),
    age: pickRandomInt(AGE_MIN, AGE_MAX),
    hairStyle: pickRandom(HAIR_STYLE_OPTIONS).value,
    hairColor: pickRandom(HAIR_COLORS).value,
    eyeColor: pickRandom(EYE_COLORS).value,
    bodyType: pickRandom(BODY_TYPE_OPTIONS).value,
    breastSize: pickRandom(BREAST_SIZE_OPTIONS).value,
    buttSize: pickRandom(BUTT_SIZE_OPTIONS).value,
    personality,
    voiceId: pickRandom(VOICE_FALLBACK_OPTIONS).id,
    occupation: pickRandomOptionValue(OCCUPATION_OPTIONS),
    relationshipType: pickRandom(RELATIONSHIP_OPTIONS).value,
    hobby: pickRandomOptionValue(HOBBY_OPTIONS),
    fetish: pickRandomOptionValue(FETISH_OPTIONS),
    style: "",
  }
}
